import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
    BehaviorSubject,
    EMPTY,
    Observable,
    Subscription,
    defer,
    fromEvent,
    merge,
    timer
} from "rxjs";
import { catchError, exhaustMap, filter, finalize, map, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import {
    isCanonicalTaskRouteKey, normalizeAssessmentTaskId
} from "../utils/assessment_task_route.util";

export type UserNotificationStatus = "unread" | "read" | "archived";
export type UserNotificationRouteType = "assessment" | "evidence_requirement_upload" | "evidence_requirement_item" | "secure_storage_manage_files" | "secure_storage_dashboard" | "user_settings";

export interface UserNotificationLink {
    routeType: UserNotificationRouteType;
    linkText: string;
    productLicenseId: number | null;
    taskId: number | null;
    merchantId: number | null;
    evidenceRequirementId: number | null;
    formKey: "roc_form" | "saq_form" | null;
    activeSection: string | null;
    activeRequirement: string | null;
}

export interface EvidenceReminderTarget {
    readonly merchantId: number;
    readonly productLicenseId: number;
    readonly evidenceRequirementId: number;
}

function positiveNumericId(value: unknown): value is number {
    return typeof value === "number" && Number.isSafeInteger(value) && value > 0;
}

// A reminder descriptor is only an intent to open an item. The exact read
// still decides whether the current user may see it.
export function extractEvidenceReminderTarget(value: unknown): EvidenceReminderTarget | null {
    if (!value || typeof value !== "object" || Array.isArray(value)) return null;
    const link = value as Record<string, unknown>;
    const merchantId = link["merchantId"];
    const productLicenseId = link["productLicenseId"];
    const evidenceRequirementId = link["evidenceRequirementId"];
    if (link["routeType"] !== "evidence_requirement_item" ||
        link["linkText"] !== "View evidence" ||
        !positiveNumericId(merchantId) ||
        !positiveNumericId(productLicenseId) ||
        !positiveNumericId(evidenceRequirementId)) return null;

    return { merchantId, productLicenseId, evidenceRequirementId };
}

export interface UserNotification {
    id: number;
    notificationType: string;
    title: string;
    message: string;
    status: UserNotificationStatus;
    createdAt: string | null;
    link: UserNotificationLink | null;
}

export interface UserNotificationListState {
    items: UserNotification[];
    unreadCount: number;
}

export interface UserNotificationState extends UserNotificationListState {
    isLoading: boolean;
}

interface ApiResponse<T> {
    success: boolean;
    data?: T;
    message?: string;
}

@Injectable({ providedIn: "root" })
export class UserNotificationsService {
    private readonly endpoint = `${environment.baseurl.replace(/\/$/, "")}/user-notifications`;
    private readonly notificationState = new BehaviorSubject<UserNotificationState>({
        items: [],
        unreadCount: 0,
        isLoading: false
    });
    private summaryPollingSubscription: Subscription | null = null;
    private visibilityRefreshSubscription: Subscription | null = null;

    constructor(private readonly http: HttpClient) {}

    watchNotificationState(): Observable<UserNotificationState> {
        return this.notificationState.asObservable();
    }

    refreshCurrentUserNotifications(): Observable<UserNotificationListState> {
        return defer(() => {
            this.updateState({ isLoading: true });
            return this.http.get<ApiResponse<unknown>>(this.endpoint).pipe(
                map((response) => this.requireListState(response)),
                tap((state) => {
                    const displayedStatuses = new Map(
                        this.notificationState.value.items.map((notification) => [notification.id, notification.status])
                    );
                    this.updateState({
                        ...state,
                        items: state.items.map((notification) => ({
                            ...notification,
                            status: displayedStatuses.get(notification.id) || notification.status
                        }))
                    });
                }),
                finalize(() => this.updateState({ isLoading: false }))
            );
        });
    }

    refreshCurrentUserNotificationSummary(): Observable<number> {
        return this.http
            .get<ApiResponse<unknown>>(`${this.endpoint}/summary`)
            .pipe(
                map((response) => this.requireUnreadCount(response)),
                tap((unreadCount) => this.updateState({ unreadCount }))
            );
    }

    startSummaryPolling(intervalMs: number = 60_000): void {
        this.stopSummaryPolling();
        if (typeof window === "undefined" || typeof document === "undefined") {
            return;
        }

        const normalizedIntervalMs = Number.isSafeInteger(intervalMs) && intervalMs >= 1_000
            ? intervalMs
            : 60_000;
        this.summaryPollingSubscription = timer(normalizedIntervalMs, normalizedIntervalMs)
            .pipe(
                filter(() => document.visibilityState === "visible"),
                exhaustMap(() => this.refreshCurrentUserNotificationSummary().pipe(catchError(() => EMPTY)))
            )
            .subscribe();
        this.visibilityRefreshSubscription = merge(
            fromEvent(window, "focus"),
            fromEvent(document, "visibilitychange").pipe(filter(() => document.visibilityState === "visible"))
        )
            .pipe(exhaustMap(() => this.refreshCurrentUserNotificationSummary().pipe(catchError(() => EMPTY))))
            .subscribe();
    }

    stopSummaryPolling(): void {
        this.summaryPollingSubscription?.unsubscribe();
        this.summaryPollingSubscription = null;
        this.visibilityRefreshSubscription?.unsubscribe();
        this.visibilityRefreshSubscription = null;
    }

    clearNotificationState(): void {
        this.stopSummaryPolling();
        this.notificationState.next({ items: [], unreadCount: 0, isLoading: false });
    }

    markCurrentUserNotificationsRead(): Observable<number> {
        return this.http
            .patch<ApiResponse<{ markedReadCount?: unknown }>>(`${this.endpoint}/read`, {})
            .pipe(map((response) => {
                this.requireSuccess(response, "Unable to mark notifications as read.");
                const count = Number(response.data?.markedReadCount);
                return Number.isSafeInteger(count) && count >= 0 ? count : 0;
            }));
    }

    archiveNotification(notificationId: number): Observable<void> {
        if (!Number.isSafeInteger(notificationId) || notificationId <= 0) {
            throw new Error("A valid notification identifier is required.");
        }

        return this.http
            .patch<ApiResponse<unknown>>(`${this.endpoint}/${notificationId}/archive`, {})
            .pipe(map((response) => {
                this.requireSuccess(response, "Unable to archive notification.");
            }), tap(() => {
                const state = this.notificationState.value;
                const notification = state.items.find((item) => item.id === notificationId);
                this.updateState({
                    items: state.items.filter((item) => item.id !== notificationId),
                    unreadCount: notification?.status === "unread"
                        ? Math.max(0, state.unreadCount - 1)
                        : state.unreadCount
                });
            }));
    }

    private requireListState(response: ApiResponse<unknown>): UserNotificationListState {
        this.requireSuccess(response, "Unable to load notifications.");
        const data = response.data as { items?: unknown; unreadCount?: unknown } | undefined;
        const items = Array.isArray(data?.items)
            ? data.items.map((item) => this.normalizeNotification(item)).filter((item): item is UserNotification => item !== null)
            : [];
        const unreadCount = Number(data?.unreadCount);
        return {
            items,
            unreadCount: Number.isSafeInteger(unreadCount) && unreadCount >= 0 ? unreadCount : 0
        };
    }

    private requireUnreadCount(response: ApiResponse<unknown>): number {
        this.requireSuccess(response, "Unable to load notification summary.");
        const data = response.data as { unreadCount?: unknown } | undefined;
        const unreadCount = Number(data?.unreadCount);
        return Number.isSafeInteger(unreadCount) && unreadCount >= 0 ? unreadCount : 0;
    }

    private updateState(update: Partial<UserNotificationState>): void {
        this.notificationState.next({ ...this.notificationState.value, ...update });
    }

    private normalizeNotification(value: unknown): UserNotification | null {
        if (!value || typeof value !== "object" || Array.isArray(value)) {
            return null;
        }

        const record = value as Record<string, unknown>;
        const id = Number(record["id"]);
        const notificationType = typeof record["notificationType"] === "string" ? record["notificationType"].trim() : "";
        const title = typeof record["title"] === "string" ? record["title"].trim() : "";
        const message = typeof record["message"] === "string" ? record["message"].trim() : "";
        const status = record["status"];
        if (
            !Number.isSafeInteger(id) || id <= 0 || !notificationType || !title ||
            (status !== "unread" && status !== "read" && status !== "archived")
        ) {
            return null;
        }

        return {
            id,
            notificationType,
            title,
            message,
            status,
            createdAt: typeof record["createdAt"] === "string" ? record["createdAt"] : null,
            link: this.normalizeLink(record["link"])
        };
    }

    private normalizeLink(value: unknown): UserNotificationLink | null {
        if (!value || typeof value !== "object" || Array.isArray(value)) {
            return null;
        }
        const record = value as Record<string, unknown>;
        const routeType = record["routeType"];
        const isEvidenceRoute = routeType === "evidence_requirement_upload" ||
            routeType === "evidence_requirement_item";
        const linkText = typeof record["linkText"] === "string" ? record["linkText"].trim() : "";
        const productLicenseId = Number(record["productLicenseId"]);
        const taskId = normalizeAssessmentTaskId(record["taskId"]);
        const merchantId = Number(record["merchantId"]);
        const rawEvidenceRequirementId = record["evidenceRequirementId"];
        const evidenceRequirementId = positiveNumericId(rawEvidenceRequirementId)
            ? rawEvidenceRequirementId : null;
        const formKey = record["formKey"];
        const activeSection = typeof record["activeSection"] === "string" ? record["activeSection"].trim() : "";
        const activeRequirement = typeof record["activeRequirement"] === "string" ? record["activeRequirement"].trim() : "";
        if (
            (routeType !== "assessment" && routeType !== "evidence_requirement_upload" &&
                routeType !== "evidence_requirement_item" &&
                routeType !== "secure_storage_manage_files" &&
                routeType !== "secure_storage_dashboard" && routeType !== "user_settings") || !linkText
        ) {
            return null;
        }
        if (routeType === "evidence_requirement_item" && !extractEvidenceReminderTarget(record)) {
            return null;
        }
        if (routeType === "evidence_requirement_upload" && (
            !positiveNumericId(record["productLicenseId"]) ||
            !positiveNumericId(record["merchantId"]) ||
            evidenceRequirementId === null
        )) {
            return null;
        }
        return {
            routeType,
            linkText,
            productLicenseId: Number.isSafeInteger(productLicenseId) && productLicenseId > 0 ? productLicenseId : null,
            taskId: routeType === "assessment" &&
                Number.isSafeInteger(productLicenseId) && productLicenseId > 0 &&
                (formKey === "roc_form" || formKey === "saq_form") &&
                isCanonicalTaskRouteKey(activeSection) &&
                isCanonicalTaskRouteKey(activeRequirement) ? taskId : null,
            merchantId: Number.isSafeInteger(merchantId) && merchantId > 0 ? merchantId : null,
            evidenceRequirementId: isEvidenceRoute ? evidenceRequirementId : null,
            formKey: routeType !== "evidence_requirement_item" &&
                (formKey === "roc_form" || formKey === "saq_form") ? formKey : null,
            activeSection: routeType === "evidence_requirement_item" ? null : activeSection || null,
            activeRequirement: routeType === "evidence_requirement_item" ? null : activeRequirement || null
        };
    }

    private requireSuccess(response: ApiResponse<unknown>, fallbackMessage: string): void {
        if (!response?.success) {
            throw new Error(response?.message || fallbackMessage);
        }
    }
}
